import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticsEventsMapping, AnalyticsManager, AnalyticsProviderProps } from './types';
import { AnalyticsEvents } from './constants';
import { AnalyticsContext } from './AnalyticsContext';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { userSelector } from 'src/redux/user/selector';
import { experimentVariantSelector } from 'src/redux/onBoardingFlow/selector';
import { getDeviceType } from 'src/shared/utils/deviceType';
import { newOnBoardingDataSelector } from 'src/redux/auth/selector';

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children, services }) => {
  const [globalProps, setGlobalProps] = useState<Record<string, string>>({});

  const databaseStoredUser = useSelector(userSelector);
  const selectedExperimentVariant = useSelector(experimentVariantSelector);
  const newOnBoardingData = useSelector(newOnBoardingDataSelector);

  const {
    i18n: { language },
  } = useTranslation();

  // Initialize all services when the provider mounts.
  useEffect(() => {
    services.forEach((service) => {
      service.initialize();
    });
  }, [services]);

  useEffect(() => {
    setGlobalProps((prev) => ({
      ...prev,
      locale: language,
      device_type: getDeviceType(),
    }));
  }, [language]);

  useEffect(() => {
    setGlobalProps((prev) => {
      const updatedProps = { ...prev };

      // Add or update properties if they exist
      if (databaseStoredUser?.onboarding_key) updatedProps.funnel_id = databaseStoredUser.onboarding_key;
      else delete updatedProps.funnel_id;

      if (databaseStoredUser?.onboarding_name) updatedProps.funnel_name = databaseStoredUser.onboarding_name;
      else delete updatedProps.funnel_name;

      if (databaseStoredUser?.onboarding_type) updatedProps.funnel_type = databaseStoredUser.onboarding_type;
      else delete updatedProps.funnel_type;

      if (databaseStoredUser?.onboarding_experiment_name && selectedExperimentVariant === null) {
        delete updatedProps.onboarding_experiment_name;
      } else if (databaseStoredUser?.onboarding_experiment_name && !selectedExperimentVariant?.name) {
        updatedProps.onboarding_experiment_name = databaseStoredUser.onboarding_experiment_name;
      } else if (databaseStoredUser?.onboarding_experiment_name && selectedExperimentVariant?.name) {
        updatedProps.onboarding_experiment_name = selectedExperimentVariant.name;
      } else if (databaseStoredUser?.onboarding_experiment_name) {
        updatedProps.onboarding_experiment_name = databaseStoredUser.onboarding_experiment_name;
      } else if (selectedExperimentVariant?.name) {
        updatedProps.onboarding_experiment_name = selectedExperimentVariant.name;
      } else if (newOnBoardingData?.experiment) {
        updatedProps.onboarding_experiment_name = newOnBoardingData.experiment;
      } else {
        delete updatedProps.onboarding_experiment_name;
      }

      return updatedProps;
    });
  }, [databaseStoredUser, selectedExperimentVariant, newOnBoardingData]);

  useEffect(() => {
    if (databaseStoredUser) {
      setUserProperties({
        $email: databaseStoredUser?.email,
        $first_name: databaseStoredUser?.display_name,
        $last_name: databaseStoredUser?.last_name,
      });
    }
  }, [databaseStoredUser?.email, databaseStoredUser?.display_name, databaseStoredUser?.last_name]);

  /**
   * Helper function to check if a service should be targeted based on targetServices parameter
   *
   * @param serviceId - The ID of the service to check
   * @param targetServices - Optional array of service IDs to target
   * @returns Boolean indicating if the service should be targeted
   */
  const shouldTargetService = useCallback((serviceId: string, targetServices?: string[]) => {
    // If no target services specified, target all services
    if (!targetServices?.length) {
      return true;
    }

    // Otherwise, only target services in the target list
    return targetServices.includes(serviceId);
  }, []);

  const identify = useCallback(
    (userId: string, properties?: Record<string, any>, targetServices?: string[]) => {
      services.forEach((service) => {
        if (service.identify && shouldTargetService(service.id, targetServices)) {
          service.identify(userId, properties);
        }
      });
    },
    [services, shouldTargetService]
  );

  const reset = useCallback(() => {
    services.forEach((service) => {
      if (service.reset) {
        service.reset();
      }
    });
  }, [services]);

  const trackPredefinedEvent = useCallback(
    <K extends keyof AnalyticsEventsMapping>(
      eventKey: K,
      overrideProps?: Partial<AnalyticsEventsMapping[K]['props']>,
      targetServices?: string[]
    ) => {
      const eventConfig = AnalyticsEvents[eventKey];
      if (!eventConfig) {
        console.error(`No event config found for key: ${eventKey}`);
        return;
      }

      const mergedProperties = {
        ...(!eventConfig?.ignoreGlobalProps && globalProps),
        ...overrideProps,
      };

      // Filter out properties that should be ignored based on ignoreGloablEventProps
      const filteredProperties = eventConfig.ignoreGloablEventProps?.length
        ? Object.fromEntries(
            Object.entries(mergedProperties).filter(([key]) => !eventConfig.ignoreGloablEventProps?.includes(key))
          )
        : mergedProperties;

      const finalTargetServices = targetServices ?? eventConfig.services ?? [];

      services.forEach((service) => {
        if (shouldTargetService(service.id, finalTargetServices)) {
          service.sendEvent(eventConfig.eventName, filteredProperties);
        }
      });
    },
    [services, globalProps, shouldTargetService]
  );

  const incrementUserProperty = useCallback(
    (property: string, value: number) => {
      services.forEach((service) => {
        if (service.incrementUserProperty) {
          service.incrementUserProperty(property, value);
        }
      });
    },
    [services]
  );

  const setSuperProperties = useCallback(
    (properties: Record<string, any>, targetServices?: string[]) => {
      services.forEach((service) => {
        if (service.setSuperProperties && shouldTargetService(service.id, targetServices)) {
          service.setSuperProperties(properties);
        }
      });
    },
    [services, shouldTargetService]
  );

  const setSuperPropertiesOnce = useCallback(
    (properties: Record<string, any>, targetServices?: string[]) => {
      services.forEach((service) => {
        if (service.setSuperPropertiesOnce && shouldTargetService(service.id, targetServices)) {
          service.setSuperPropertiesOnce(properties);
        }
      });
    },
    [services, shouldTargetService]
  );

  const setUserProperties = useCallback(
    (properties: Record<string, any>, targetServices?: string[]) => {
      services.forEach((service) => {
        if (service.setUserProperties && shouldTargetService(service.id, targetServices)) {
          service.setUserProperties(properties);
        }
      });
    },
    [services, shouldTargetService]
  );

  const setUserPropertiesOnce = useCallback(
    (properties: Record<string, any>, targetServices?: string[]) => {
      services.forEach((service) => {
        if (service.setUserPropertiesOnce && shouldTargetService(service.id, targetServices)) {
          service.setUserPropertiesOnce(properties);
        }
      });
    },
    [services, shouldTargetService]
  );

  const manager: AnalyticsManager = useMemo(
    () => ({
      identify,
      reset,
      trackPredefinedEvent,
      incrementUserProperty,
      setSuperProperties,
      setSuperPropertiesOnce,
      setUserProperties,
      setUserPropertiesOnce,
    }),
    [
      identify,
      trackPredefinedEvent,
      reset,
      incrementUserProperty,
      setSuperProperties,
      setSuperPropertiesOnce,
      setUserProperties,
      setUserPropertiesOnce,
    ]
  );

  return <AnalyticsContext.Provider value={manager}>{children}</AnalyticsContext.Provider>;
};
